import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock } from 'lucide-react';
import { blogPosts } from '../data/blogPosts';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';

const BlogIndex = () => {
  // Post destacado (el más reciente)
  const featuredPost = blogPosts[0];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <SEOHead 
        title="Edutech Pulse Lab | Reflexiones sobre decisión, aprendizaje y complejidad"
        description="Artículos y notas del laboratorio sobre toma de decisiones, liderazgo, innovación y aprendizaje aplicado en contextos reales."
        keywords="toma decisiones, liderazgo, innovación, aprendizaje aplicado, complejidad, pensamiento crítico"
        canonicalUrl="/blog"
      />

      <Navbar />

      <section className="bg-gradient-to-br from-[#0c203b] via-[#1a365d] to-[#2d4a6b] text-white py-10 sm:py-16 lg:py-20">
        {/* Header */}
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-[1.15] tracking-[-0.01em] sm:tracking-[-0.02em]">
            <span className="bg-gradient-to-r from-blue-200 via-white to-purple-200 bg-clip-text text-transparent font-black">
              Blog
            </span>
            <span className="text-white/95 font-light"> Edutech Pulse Lab</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-blue-100/85 mb-8 sm:mb-10 leading-relaxed font-light max-w-3xl">
            Notas del laboratorio sobre <span className="font-medium text-white/95">toma de decisiones, liderazgo y aprendizaje</span> en contextos reales
          </p>
        </div>

      </section>

      {/* Post destacado */}
      {featuredPost && (
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-12 sm:py-16">
          <div className="bg-slate-50 rounded-2xl p-6 sm:p-8 md:p-10 border-2 border-gray-200 shadow-md hover:shadow-lg transition-all duration-300">
            <div className="flex items-center gap-2 mb-4 sm:mb-5">
              <span className="px-3 py-1 bg-[#0c203b] text-white text-xs font-medium rounded-md">
                Destacado
              </span>
              <span className="px-3 py-1 border border-slate-300 text-slate-600 text-xs font-medium rounded-md">
                {featuredPost.category}
              </span>
            </div>

            <Link to={`/blog/${featuredPost.slug}`}>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-[#0c203b] mb-4 hover:text-slate-700 transition-colors leading-tight">
                {featuredPost.title}
              </h2>
            </Link>

            <p className="text-slate-600 text-base sm:text-lg mb-6 leading-relaxed">
              {featuredPost.excerpt}
            </p>

            <div className="flex flex-wrap items-center gap-4 mb-6 text-xs sm:text-sm text-slate-500">
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                <span>{new Date(featuredPost.publishedDate).toLocaleDateString('es-ES', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                <span>{featuredPost.readTime}</span>
              </div>
            </div>

            <Link 
              to={`/blog/${featuredPost.slug}`}
              className="inline-flex items-center gap-2 text-[#0c203b] font-medium hover:gap-3 transition-all text-sm sm:text-base"
            >
              Leer artículo completo
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}

      {/* Grid de artículos */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 pb-12 sm:pb-16 lg:pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {blogPosts.map((post) => (
            <article 
              key={post.slug}
              className="bg-white rounded-xl p-5 sm:p-6 border-2 border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 group"
            >
              {/* Categoría */}
              <div className="mb-3">
                <span className="px-2.5 py-0.5 border border-slate-300 text-slate-600 text-xs font-medium rounded">
                  {post.category}
                </span>
              </div>

              {/* Título */}
              <Link to={`/blog/${post.slug}`}>
                <h3 className="text-lg sm:text-xl font-semibold text-[#0c203b] mb-3 group-hover:text-slate-700 transition-colors line-clamp-2 leading-snug">
                  {post.title}
                </h3>
              </Link>

              {/* Excerpt */}
              <p className="text-slate-600 text-sm mb-5 line-clamp-3 leading-relaxed">
                {post.excerpt}
              </p>

              {/* Meta info */}
              <div className="flex items-center gap-3 text-xs text-slate-500 mb-4">
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-3 h-3" />
                  <span>{new Date(post.publishedDate).toLocaleDateString('es-ES', { 
                    month: 'short', 
                    day: 'numeric' 
                  })}</span>
                </div>
                <span>·</span>
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3 h-3" />
                  <span>{post.readTime}</span>
                </div>
              </div>

              {/* CTA */}
              <Link 
                to={`/blog/${post.slug}`}
                className="inline-flex items-center gap-2 text-[#0c203b] font-medium text-sm hover:gap-3 transition-all"
              >
                Leer más
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </article>
          ))}
        </div>


      </div>

      {/* CTA footer */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 pb-12 sm:pb-16 lg:pb-20">
        <div className="bg-slate-100 rounded-2xl p-8 sm:p-10 md:p-12 text-center shadow-md">
          <h2 className="text-2xl sm:text-3xl font-semibold text-[#0c203b] mb-3 sm:mb-4 leading-tight">
            ¿Listo para llevar tu carrera al siguiente nivel?
          </h2>
          <p className="text-base sm:text-lg text-slate-600 mb-6 sm:mb-8 max-w-2xl mx-auto">
            Explora nuestros micro-programas especializados y aprende con metodologías probadas
          </p>
          <Link
            to="/programas"
            className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 bg-[#0c203b] text-white font-medium rounded-lg hover:bg-slate-800 transition-colors text-sm sm:text-base"
          >
            Ver Micro-Programas
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BlogIndex;
